import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { useNavigate, Link } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Users, Loader2, LinkIcon, CheckCircle } from 'lucide-react';
import { useAuth } from '@/features/auth';
import { api } from '@watchparty/shared';
export function JoinPartyPage({ token }) {
    const navigate = useNavigate();
    const auth = useAuth();
    const queryClient = useQueryClient();
    const [pageState, setPageState] = useState('loading');
    const [invite, setInvite] = useState(null);
    const [partyId, setPartyId] = useState(null);
    const [isJoining, setIsJoining] = useState(false);
    const [error, setError] = useState(null);
    // If not authenticated, save token and redirect to landing
    useEffect(() => {
        if (!auth.isLoading && !auth.user) {
            sessionStorage.setItem('pendingJoinToken', token);
            navigate({ to: '/' });
        }
    }, [auth.isLoading, auth.user, token, navigate]);
    // Fetch invite details once authenticated
    useEffect(() => {
        if (!auth.user)
            return;
        let cancelled = false;
        async function fetchInvite() {
            try {
                const res = await api.get(`/parties/invite/${token}`);
                if (cancelled)
                    return;
                setInvite(res);
                setPageState('confirm');
            }
            catch {
                if (!cancelled)
                    setPageState('expired');
            }
        }
        fetchInvite();
        return () => { cancelled = true; };
    }, [auth.user, token]);
    async function handleAccept() {
        setIsJoining(true);
        setError(null);
        try {
            const res = await api.post(`/parties/invite/${token}/accept`);
            queryClient.invalidateQueries({ queryKey: ['parties'] });
            if (res.alreadyMember) {
                setPartyId(res.partyId);
                setPageState('alreadyMember');
            }
            else {
                navigate({ to: '/parties/$partyId', params: { partyId: res.partyId } });
            }
        }
        catch (err) {
            const message = err instanceof Error ? err.message : '';
            if (message === 'expired') {
                setPageState('expired');
            }
            else {
                setError('Something went wrong. Please try again.');
            }
            setIsJoining(false);
        }
    }
    // Don't render anything until auth state is known
    if (auth.isLoading || !auth.user) {
        return (_jsx("div", { style: {
                minHeight: '100dvh',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'var(--color-bg)',
            }, children: _jsx(Loader2, { size: 24, style: { color: 'var(--color-text-muted)', animation: 'spin 1s linear infinite' } }) }));
    }
    // Loading state
    if (pageState === 'loading') {
        return (_jsx("div", { style: {
                minHeight: '100dvh',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'var(--color-bg)',
            }, children: _jsx(Loader2, { size: 24, style: { color: 'var(--color-text-muted)', animation: 'spin 1s linear infinite' } }) }));
    }
    // Expired state
    if (pageState === 'expired') {
        return (_jsx("div", { style: {
                minHeight: '100dvh',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'var(--color-bg)',
                padding: '24px',
            }, children: _jsxs("div", { style: {
                    maxWidth: '420px', width: '100%',
                    background: 'var(--color-surface)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-xl)',
                    padding: '40px 32px',
                    textAlign: 'center',
                }, children: [_jsx("div", { style: {
                            width: '64px', height: '64px', borderRadius: '50%',
                            background: 'var(--color-surface-sunken)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto 24px',
                        }, children: _jsx(LinkIcon, { size: 28, style: { color: 'var(--color-text-muted)' } }) }), _jsx("h1", { style: {
                            fontFamily: 'var(--font-family-heading)',
                            fontSize: '1.25rem', fontWeight: 700,
                            color: 'var(--color-text)',
                            margin: '0 0 8px',
                            lineHeight: 1.3,
                        }, children: "This invite link has expired." }), _jsx("p", { style: {
                            fontFamily: 'var(--font-family-body)',
                            fontSize: '0.875rem',
                            color: 'var(--color-text-muted)',
                            margin: '0 0 32px',
                        }, children: "Ask the party owner to generate a new invite link and try again." }), _jsx(Link, { to: "/parties", style: {
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: '100%',
                            padding: '12px',
                            borderRadius: 'var(--radius-md)',
                            border: '1px solid var(--color-border)',
                            background: 'var(--color-surface)',
                            color: 'var(--color-text)',
                            fontFamily: 'var(--font-family-body)',
                            fontSize: '0.9375rem', fontWeight: 700,
                            textDecoration: 'none',
                            transition: 'background 80ms ease',
                        }, children: "Go to My Parties" })] }) }));
    }
    // Confirmation state
    return (_jsxs("div", { style: {
            minHeight: '100dvh',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'var(--color-bg)',
            padding: '24px',
        }, children: [_jsxs("div", { style: {
                    maxWidth: '420px', width: '100%',
                    background: 'var(--color-surface)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-xl)',
                    padding: '40px 32px',
                    textAlign: 'center',
                }, children: [_jsx("div", { style: {
                            width: '64px', height: '64px',
                            borderRadius: 'var(--radius-lg)',
                            background: 'var(--color-surface-sunken)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto 24px',
                        }, children: _jsx(Users, { size: 28, style: { color: 'var(--color-text-muted)' } }) }), _jsxs("h1", { style: {
                            fontFamily: 'var(--font-family-heading)',
                            fontSize: '1.25rem', fontWeight: 700,
                            color: 'var(--color-text)',
                            margin: '0 0 8px',
                            lineHeight: 1.3,
                        }, children: ["You've been invited to join ", invite?.ownerDisplayName, "'s WatchParty, ", invite?.partyName, "!"] }), _jsx("p", { style: {
                            fontFamily: 'var(--font-family-body)',
                            fontSize: '0.875rem',
                            color: 'var(--color-text-muted)',
                            margin: '0 0 32px',
                        }, children: "Watch whenever, track together." }), error && (_jsx("p", { style: {
                            fontFamily: 'var(--font-family-body)',
                            fontSize: '0.875rem',
                            color: 'var(--color-danger)',
                            margin: '0 0 16px',
                        }, children: error })), _jsx("button", { onClick: handleAccept, disabled: isJoining, style: {
                            width: '100%',
                            padding: '12px',
                            borderRadius: 'var(--radius-md)',
                            border: 'none',
                            background: 'var(--color-text)',
                            color: 'var(--color-bg)',
                            fontFamily: 'var(--font-family-body)',
                            fontSize: '0.9375rem', fontWeight: 700,
                            cursor: isJoining ? 'not-allowed' : 'pointer',
                            opacity: isJoining ? 0.7 : 1,
                            transition: 'opacity 80ms ease',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                        }, children: isJoining
                            ? _jsxs(_Fragment, { children: [_jsx(Loader2, { size: 16, style: { animation: 'spin 1s linear infinite' } }), " Joining..."] })
                            : 'Accept Invitation' })] }), pageState === 'alreadyMember' && partyId && (_jsx("div", { style: {
                    position: 'fixed', inset: 0, zIndex: 100,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '24px',
                }, children: _jsxs("div", { style: {
                        maxWidth: '360px', width: '100%',
                        background: 'var(--color-surface)',
                        border: '1px solid var(--color-border)',
                        borderRadius: 'var(--radius-xl)',
                        padding: '32px',
                        textAlign: 'center',
                    }, children: [_jsx("div", { style: {
                                width: '48px', height: '48px', borderRadius: '50%',
                                background: 'var(--color-surface-sunken)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                margin: '0 auto 16px',
                            }, children: _jsx(CheckCircle, { size: 22, style: { color: 'var(--color-text-muted)' } }) }), _jsx("h2", { style: {
                                fontFamily: 'var(--font-family-heading)',
                                fontSize: '1.1rem', fontWeight: 700,
                                color: 'var(--color-text)',
                                margin: '0 0 8px',
                            }, children: "You're already in this party." }), _jsx("p", { style: {
                                fontFamily: 'var(--font-family-body)',
                                fontSize: '0.875rem',
                                color: 'var(--color-text-muted)',
                                margin: '0 0 24px',
                            }, children: "Head to the party to see what's on the watch list." }), _jsx("button", { onClick: () => navigate({ to: '/parties/$partyId', params: { partyId } }), style: {
                                width: '100%',
                                padding: '12px',
                                borderRadius: 'var(--radius-md)',
                                border: 'none',
                                background: 'var(--color-text)',
                                color: 'var(--color-bg)',
                                fontFamily: 'var(--font-family-body)',
                                fontSize: '0.9375rem', fontWeight: 700,
                                cursor: 'pointer',
                                transition: 'opacity 80ms ease',
                            }, children: "Go to Party" })] }) }))] }));
}
